import { zeroAddress } from 'viem'
import { tellerConfigByChain } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import type { FixedTermInfo } from '../../../types/lenderTypes'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { tellerBpsToPercent, tellerImpliedLtv, tellerToHuman } from './math'
import type { TellerMarketsRaw } from './types'

/** Synthesized per-pool lender key, e.g. `TELLER_<POOL_ADDRESS_HEX_UPPER>`. */
export function tellerLenderKey(pool: string): string {
  const body = pool.startsWith('0x') ? pool.slice(2) : pool
  return 'TELLER_' + body.toUpperCase()
}

/** Recover the pool address from a `TELLER_<HEX>` lender key (or undefined). */
export function tellerPoolFromLenderKey(lender: string): string | undefined {
  if (!lender.startsWith('TELLER_')) return undefined
  const body = lender.slice('TELLER_'.length)
  return body.length === 40 ? '0x' + body.toLowerCase() : undefined
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/** Seconds → a short human duration for warnings (e.g. "5 minutes", "1 day"). */
function fmtDuration(sec: number | null | undefined): string {
  if (sec == null || sec <= 0) return 'a short window'
  if (sec < 3600) return `${Math.round(sec / 60)} minute${sec < 120 ? '' : 's'}`
  if (sec < 86400) return `${Math.round(sec / 3600)} hour${sec < 7200 ? '' : 's'}`
  return `${Math.round(sec / 86400)} day${sec < 172800 ? '' : 's'}`
}

function priceFor(
  address: string,
  chainId: string,
  tokens: GenericTokenList,
  prices: { [a: string]: number },
): number {
  const lower = address.toLowerCase()
  const token = tokens[lower]
  const key = toOracleKey(token?.assetGroup!) || toGenericPriceKey(lower, chainId)
  return prices[key] ?? 0
}

/**
 * Map the on-chain Teller pool batch into the shared `MorphoGeneralPublicResponse`
 * shape (identical to Exactly/Midnight/Term/River), keyed by
 * `TELLER_<POOL_ADDRESS>` — one key per `LenderCommitmentGroup` pool.
 *
 * Per pool (a fixed principal↔collateral pair):
 *  - a LOAN entry on the principal token: `borrowLiquidity` =
 *    `getPrincipalAmountAvailableToBorrow` (the liquidity gate); the fixed borrow
 *    APR sits on `stableBorrowRate` (fixed-rate convention, like Exactly/Term/
 *    Lista — `variableBorrowRate` stays 0, Teller has no floating leg);
 *  - a COLLATERAL entry on the collateral token with the implied origination LTV
 *    and `liquidationPenalty: 0` (Teller liquidation is TIME-based — a missed
 *    payment past the market window seizes collateral, there is no price-based
 *    penalty parameter);
 *  - `params.market.fixedTerm` = `{ model: 'teller', earlyRepay: 'none' (full
 *    early repay is pro-rata, penalty-free), provider: { kind: 'pool' } }` — a
 *    rolling duration (borrower picks ≤ `maxLoanDuration`), so no market-level
 *    calendar maturity.
 */
export function convertTellerMarketsToResponse(
  raw: TellerMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const proto = tellerConfigByChain(chainId)

  for (const p of raw?.pools ?? []) {
    const cfg = p.config
    const lenderKey = tellerLenderKey(cfg.pool)
    const principalAddr = cfg.principal.toLowerCase()
    const collateralAddr = cfg.collateral.toLowerCase()

    const principalPrice = priceFor(principalAddr, chainId, tokens, prices)
    const collateralPrice = priceFor(collateralAddr, chainId, tokens, prices)

    const borrowLiquidity = tellerToHuman(p.available, cfg.principalDecimals)
    // CURRENT principal TVL: totalAssets (ERC-4626 V2/V3) is authoritative;
    // `committed` is a cumulative lifetime counter (overstates deposits) — use
    // it only on V1 pools where totalAssets reverts.
    const totalDeposits = tellerToHuman(
      p.totalAssets ?? p.committed,
      cfg.principalDecimals,
    )
    const totalDebt = Math.max(0, totalDeposits - borrowLiquidity)
    const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0
    const borrowApr = tellerBpsToPercent(p.minRateBps)
    // Approx supply APY: utilization-weighted borrow rate (ignores protocol fee).
    const depositApr = utilization * borrowApr

    const ltv = tellerImpliedLtv(
      p.collateralPerPrincipal,
      cfg.collateralDecimals,
      principalPrice,
      collateralPrice,
    )

    // On-chain maxLoanDuration is authoritative; fall back to the config seed.
    const durationSecs = p.maxLoanDuration ?? cfg.maxLoanDuration ?? 0
    const terms = [
      {
        // Rolling duration → use the max duration as the (stable) term id.
        termId: durationSecs,
        durationSecs,
        durationDays: durationSecs / 86400,
        apr: borrowApr,
        depositApr,
        available: borrowLiquidity,
      },
    ]

    // @ts-ignore — built incrementally, matches Exactly/Midnight/Term/River converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. LOAN entry — the pool's principal (borrow) token.
    const loanUid = createMarketUid(chainId, lenderKey, principalAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: cfg.principalSymbol,
      poolId: cfg.pool.toLowerCase(),
      underlying: principalAddr,
      asset: currencyFor(
        principalAddr,
        cfg.principalDecimals,
        cfg.principalSymbol,
        tokens,
      ),
      totalDeposits,
      totalDebtStable: totalDebt,
      totalDebt: 0,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * principalPrice,
      borrowLiquidityUSD: borrowLiquidity * principalPrice,
      totalDepositsUSD: totalDeposits * principalPrice,
      totalDebtStableUSD: totalDebt * principalPrice,
      totalDebtUSD: 0,
      utilization,
      depositRate: depositApr,
      // Teller has NO floating leg — the fixed borrow APR sits on the stable slot
      // (fixed-rate convention: Exactly/Term/Lista), variable stays 0.
      variableBorrowRate: 0,
      stableBorrowRate: borrowApr,
      rateModel: 'fixedTerm',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: cfg.principalDecimals,
      config: {
        0: {
          category: 0,
          // Principal token is the borrow asset, not collateral in this pool.
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: false,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      // Open markets with live liquidity are borrowable; attestation-gated
      // markets stay borrowable here (flagged in the descriptor) — the per-user
      // gate runs at borrow time.
      borrowingEnabled: borrowLiquidity > 0 && p.marketOpen !== false,
      depositsEnabled: true,
      hasStable: false,
      variableBorrowDisabled: true,
      isActive: p.marketOpen !== false,
      isFrozen: p.marketOpen === false,
    } as any

    // 2. COLLATERAL entry — the pool's collateral token (deposit-only here).
    const collUid = createMarketUid(chainId, lenderKey, collateralAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + cfg.collateralSymbol,
      poolId: cfg.pool.toLowerCase(),
      underlying: collateralAddr,
      asset: currencyFor(
        collateralAddr,
        cfg.collateralDecimals,
        cfg.collateralSymbol,
        tokens,
      ),
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: 0,
      borrowLiquidity: 0,
      totalLiquidityUSD: 0,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: cfg.collateralDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          // Time-based liquidation → no price-based penalty parameter.
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: true,
      hasStable: false,
      isActive: true,
      isFrozen: false,
    } as any

    // Fees + default-window facts (from MarketRegistry / TellerV2).
    const marketFeeBps = p.marketFeeBps ?? 0
    const protocolFeeBps = p.protocolFeeBps ?? 0
    const originationFeeBps = marketFeeBps + protocolFeeBps
    const originationFeePercent = originationFeeBps / 100
    const graceSec = p.paymentDefaultDuration
    const graceHuman = fmtDuration(graceSec)
    const ltvPct = ltv > 0 ? Math.round(ltv * 100) : undefined
    const lossX = ltv > 0 ? (1 / ltv).toFixed(1) : undefined

    // Human-readable Teller-specific implications for the demo UI to display
    // (mirrors how Midnight's order-book model is clarified). MOST CRITICAL
    // first: Teller default seizes the ENTIRE collateral, not just the debt, and
    // the grace window after the term can be very short.
    const implications: string[] = []
    implications.push(
      graceSec != null
        ? `⚠ FULL-COLLATERAL liquidation: after the loan term you have only a SHORT grace window (~${graceHuman}) to roll over or repay. Miss it and the loan DEFAULTS — a liquidator can seize your ENTIRE escrowed collateral, not just the amount owed.${lossX ? ` At ${ltvPct}% LTV that is ~${lossX}× the borrowed value lost.` : ''}`
        : `⚠ FULL-COLLATERAL liquidation: if you miss the rollover/repay deadline past the market's grace window, the loan DEFAULTS and your ENTIRE collateral can be seized — not just the amount owed.`,
    )
    implications.push(
      'TIME-based liquidation only — NO price-based liquidation and NO margin call; the ONLY trigger is a missed payment past the deadline. Set a reminder to roll over or repay on time.',
    )
    if (originationFeeBps > 0)
      implications.push(
        `Upfront origination fee of ${originationFeePercent}% is charged on the borrow (market ${marketFeeBps / 100}% + protocol ${protocolFeeBps / 100}%).`,
      )
    implications.push(
      'Fixed-term, fixed-APR: the rate is locked at borrow time for a duration you choose (up to the pool max).',
    )
    implications.push(
      'Closing is a FULL repayment: it repays principal + interest and releases ALL collateral in one tx (a partial repay keeps the collateral escrowed).',
    )
    if (p.requiresBorrowerAttestation)
      implications.push(
        'This market requires borrower ATTESTATION — the borrower must be whitelisted by the market owner before borrowing.',
      )
    if (p.marketOpen === false)
      implications.push('This market is currently CLOSED (not borrowable).')

    entry.params = {
      market: {
        lender: lenderKey,
        name: cfg.name ?? `Teller ${cfg.principalSymbol} / ${cfg.collateralSymbol}`,
        loanDecimals: cfg.principalDecimals,
        collateralDecimals: cfg.collateralDecimals,
        // Pool address doubles as the market id (it IS the lender-key body).
        id: cfg.pool.toLowerCase(),
        lltv: ltv.toString(),
        oracle: zeroAddress,
        irm: zeroAddress,
        collateralAddress: collateralAddr,
        loanAddress: principalAddr,
        terms,
        // Canonical cross-protocol fixed-term descriptor. Teller: rolling
        // duration (≤ maxLoanDuration), pool-fronted fixed APR, early repay =
        // NONE (full early repay is pro-rata, penalty-free), overdue handling is
        // time-based default/seizure (no late-penalty APR).
        fixedTerm: {
          model: 'teller',
          maturity: undefined,
          fees:
            originationFeeBps > 0 ? { originationFeePercent } : {},
          earlyRepay: { kind: 'none' },
          provider: { kind: 'pool', address: cfg.pool.toLowerCase() },
        } as FixedTermInfo,
        // --- Teller descriptor (addresses + config the calldata/worker
        // resolvers consume; mirrors river's `river` / exactly's extras) ---
        teller: {
          pool: cfg.pool.toLowerCase(),
          // On-chain marketId is authoritative; fall back to the config seed.
          marketId:
            p.marketId != null ? p.marketId.toString() : cfg.marketId,
          maxLoanDuration: durationSecs,
          // Permissioning: some markets only let PRE-ATTESTED borrowers open
          // loans (the borrow builder enforces per-user; this flags it for the
          // UI/allocator). `marketOpen` = MarketRegistry.isMarketOpen.
          requiresBorrowerAttestation: p.requiresBorrowerAttestation,
          marketOpen: p.marketOpen,
          // Fees (bps + combined percent) — upfront origination fee on the borrow.
          marketFeeBps: p.marketFeeBps,
          protocolFeeBps: p.protocolFeeBps,
          originationFeePercent:
            originationFeeBps > 0 ? originationFeePercent : undefined,
          /** Grace window (secs) after the term before DEFAULT + full-collateral
           *  liquidation. Can be very short (observed 300s = 5 min). */
          paymentDefaultDuration: p.paymentDefaultDuration,
          /** Human-readable caveats for the UI to surface (see above). */
          implications,
          minInterestRateBps: p.minRateBps ?? undefined,
          interestRateLowerBound: cfg.interestRateLowerBound ?? undefined,
          interestRateUpperBound: cfg.interestRateUpperBound ?? undefined,
          principalAvailable:
            p.available !== null ? p.available.toString() : undefined,
          collateralPerPrincipal:
            p.collateralPerPrincipal !== null
              ? p.collateralPerPrincipal.toString()
              : undefined,
          addresses: {
            tellerV2: proto?.tellerV2,
            collateralManager: proto?.collateralManager,
            smartCommitmentForwarder: proto?.smartCommitmentForwarder,
            marketRegistry: proto?.marketRegistry,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
